import {
  Description as DocumentIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { printPdfDocument } from 'containers/patient/documents/preview-document';
import { useLastOrderForm } from 'hooks/useLastOrderForm';
import React, { useState } from 'react';

const useStyles = makeStyles((theme: any) => ({
  iconButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  popover: {
    pointerEvents: 'none',
  },
  popoverContent: {
    padding: theme.spacing(2),
    maxWidth: '300px',
  },
  popoverTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  popoverItem: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  popoverLabel: {
    fontWeight: 500,
    minWidth: '100px',
  },
  popoverActions: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  clickablePopover: {
    pointerEvents: 'auto',
  },
  noDataText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface IOrderFormPreviewButtonProps {
  patientId: number;
  therapyId: number;
  customerId: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showTooltipOnHover?: boolean;
  variant?: 'icon' | 'link';
}

export const OrderFormPreviewButton: React.FC<IOrderFormPreviewButtonProps> = ({
  patientId,
  therapyId,
  customerId,
  disabled = false,
  size = 'small',
  showTooltipOnHover = true,
  variant = 'icon',
}) => {
  const classes = useStyles();
  const [opening, setOpening] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { orderForm, loading, error } = useLastOrderForm(patientId, therapyId, !disabled);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (showTooltipOnHover && !disabled && orderForm) {
      setAnchorEl(event.currentTarget);
      setPopoverOpen(true);
    }
  };

  const handleMouseLeave = () => {
    setPopoverOpen(false);
  };

  const openDocument = async () => {
    if (!orderForm || opening) return;
    setOpening(true);
    setPopoverOpen(false);
    try {
      await printPdfDocument(orderForm.documentId, customerId, patientId);
    } catch (err: any) {
      // Error handled by printPdfDocument
    } finally {
      setOpening(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    openDocument();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return dateString;
    }
  };

  const getTooltipTitle = () => {
    if (loading) return 'Loading...';
    if (error) return 'Error loading order form';
    if (!orderForm) return 'No previous order form found';
    if (opening) return 'Opening document...';
    return 'View last order form';
  };

  const getIcon = () => {
    if (loading) {
      return <CircularProgress size={16} />;
    }
    if (error) {
      return <ErrorIcon fontSize={size} color="error" />;
    }
    if (!orderForm) {
      return <InfoIcon fontSize={size} color="disabled" />;
    }
    return <DocumentIcon fontSize={size} color="primary" />;
  };

  if (!orderForm && variant === 'link') {
    return null;
  }

  if (variant === 'link' && orderForm) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            padding: '12px 16px',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            mb: 2,
            width: 'fit-content',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            FC #{orderForm.fillCycleNumber} - {formatDate(orderForm.completedDate)}
          </Typography>
          <Button
            startIcon={opening ? <CircularProgress size={14} /> : <DocumentIcon />}
            onClick={handleClick}
            disabled={disabled || loading || opening}
            size="small"
            sx={{
              'textTransform': 'none',
              'padding': 0,
              'minWidth': 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {opening ? 'Opening…' : `Order form #${orderForm.orderId}`}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Tooltip title={getTooltipTitle()} placement="top">
        <span>
          <IconButton
            className={classes.iconButton}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            disabled={disabled || loading || !orderForm}
            size={size}
            aria-label="View last order form"
          >
            {getIcon()}
          </IconButton>
        </span>
      </Tooltip>

      {showTooltipOnHover && orderForm && (
        <Popover
          className={classes.clickablePopover}
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handleMouseLeave}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          disableRestoreFocus
          sx={{ pointerEvents: 'none' }}
          slotProps={{
            paper: {
              onMouseEnter: () => setPopoverOpen(true),
              onMouseLeave: handleMouseLeave,
              sx: { pointerEvents: 'auto' },
            },
          }}
        >
          <Box className={classes.popoverContent}>
            <Typography className={classes.popoverTitle}>Last Order Form</Typography>

            <Box className={classes.popoverItem}>
              <Typography className={classes.popoverLabel}>File:</Typography>
              <Typography>{orderForm.fileName || 'N/A'}</Typography>
            </Box>

            <Box className={classes.popoverItem}>
              <Typography className={classes.popoverLabel}>Completed:</Typography>
              <Typography>{formatDate(orderForm.completedDate)}</Typography>
            </Box>

            <Box className={classes.popoverItem}>
              <Typography className={classes.popoverLabel}>Fill Cycle:</Typography>
              <Typography>#{orderForm.fillCycleNumber}</Typography>
            </Box>

            <Box className={classes.popoverItem}>
              <Typography className={classes.popoverLabel}>Order ID:</Typography>
              <Typography>{orderForm.orderId}</Typography>
            </Box>

            <Box className={classes.popoverActions}>
              <Button size="small" variant="contained" color="primary" onClick={handleClick}>
                View Full Document
              </Button>
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
};
